import moment from 'moment';
import momentTimezone from 'moment-timezone';
import _ from 'lodash';

import { AccountTypes } from '@/data/static/accountTypes';
import Actions from '@/data/static/upcomingTransactionActions';
import { timeZone as timeZoneConfig, loggly as logglyConfig } from '@/config/config';
import transactionTypes from '@/data/static/transactionTypes';
import { UserTypes } from '@/data/static/userTypes';

import restrictedStates from '@/data/static/restrictedStates';

export { getUserIdleTimeConfig } from './idle';
export { retryFn } from './helpers';

export const zipRegex = /^\d{5}$|^\d{5}-\d{4}$|^[A-Z]\d[A-Z] \d[A-Z]\d$/;

export function camelToSnakeCase(input) {
  return input.replace(/[\w]([A-Z])/g, m => `${m[0]}_${m[1]}`).toLowerCase();
}

export function timeZone() {
  return timeZoneConfig.value;
}
export function formatDate(date) {
  if (!date) {
    return '----------';
  }
  if (timeZone() === 'local') {
    return moment(date).format('MM/DD/YYYY');
  }
  return momentTimezone(date).tz(timeZone()).format('MM/DD/YYYY');
}

export function formatDecimal(number) {
  const factor = Math.pow(10, 2); // eslint-disable-line no-restricted-properties
  return Math.round(number * factor) / factor;
}

export function formatBaseAmount(amount) {
  if (amount === 0) {
    return ' 0.00';
  }
  const [whole, decimal] = amount.toFixed(2).split('.');
  const [, n1, n2] = /(\d{1,3})((?:\d{3})*)$/.exec(whole);
  const result = n2.match(/\d\d\d/g) || [];
  result.unshift(n1);
  return `${result.join(',')}.${decimal}`;
}

export function formatAmount(amount) {
  if (!amount) {
    return '$ 0.00';
  }
  const result = formatBaseAmount(amount);
  const sign = amount < 0 ? '-' : '';
  return `$${sign}${result}`;
}

export function formatNegativeAmount(amount) {
  if (amount !== 0 && !amount) {
    return '';
  }
  const result = formatBaseAmount(amount);
  const sign = amount < 0 ? '(' : '';
  const postSign = amount < 0 ? ')' : '';
  return `${sign}$${result}${postSign}`;
}

export function formatRate(rate) {
  if (!rate && rate !== 0) {
    return '';
  }
  const rateFixed = rate.toFixed(2).toString();
  return `${rateFixed}%`;
}

export function formatStatus(status) {
  return `${status[0]}${status.slice(1).toLowerCase()}`;
}

export function formatTerm(term) {
  const amountYears = term / 12;
  const yearLabel = amountYears === 1 ? 'Year' : 'Years';
  return `${amountYears} ${yearLabel}`;
}

export function formatType(type, userRole, fundingSourceId) {
  switch (type) {
    case 'ACCRINT':
      return 'Interest accrued';
    case 'FEE-SERV':
      return 'Service fee';
    case 'INT-DONATE':
      return 'Interest donated';
    case 'INT-PAID':
      return userRole === 'admin' ? 'INT-PAID' : 'Interest paid';
    case 'INT-REINVEST':
      return 'Interest reinvested in new Note';
    case 'PURCH-CANCEL':
      return 'Purchase cancelled';
    case 'PURCH-INT-REINVEST':
      return 'Note opened, interest reinvestment';
    case 'PURCH-NEW':
      return 'Note opened';
    case 'PURCH-REINVEST':
      return 'Note opened, principal reinvestment';
    case 'SELL-DONATE':
      return fundingSourceId === Actions.donate.name ? 'Amount donated at redemption' : 'Note donated at maturity';
    case 'SELL-MATURE':
      return userRole === 'admin' ? 'SELL-MATURE' : 'Note repaid';
    case 'SELL-REDEEM-FULL':
      return userRole === 'admin' ? 'SELL-REDEEM-FULL' : 'Full redemption';
    case 'SELL-REDEEM-PARTIAL':
      return userRole === 'admin' ? 'SELL-REDEEM-PARTIAL' : 'Partial redemption';
    case 'SELL-REINVEST':
      return 'Note reinvested in new Note at maturity';
    case 'SETTLEMENT':
      return 'Note opened, combined reinvestment';
    case 'REPAID-ACCT-FULL':
      return 'Redeemed / Note closed';
    case 'REPAID-ACCT-PARTIAL':
      return 'Partial redemption';
    case 'ADDITIONAL-DEPOSIT':
      return 'Additional deposit';
    case 'REPAID-ACCT':
      return 'Note repaid at maturity';
    case 'PURCH-RENEWAL':
      return 'Principal renewal';
    case 'PURCH-INT-CAP':
      return 'Interest capitalized';
    case 'PAID-INT':
      return 'Interest paid';
    case 'check':
      return 'Check';
    case 'wire/ach':
      return 'Wire/ACH';
    case 'bank':
      return 'Bank';
    case 'ACCT-TRANSFER':
      return 'Account transfer';
    case 'ADDITIONAL_DEPOSIT_AT_REINVESTMENT':
      return 'Additional deposit at reinvestment';
    default:
      return type;
  }
}

export function formatPaymentPreference(paymentPreference) {
  switch (paymentPreference) {
    case 'reinvest':
      return 'Reinvest';
    case 'payout':
      return 'Repay To Bank Account';
    case 'donate':
      return 'Donate';
    default:
      return paymentPreference;
  }
}

export function convertErrorStringsToArrayStrings(errors) {
  return errors.split(',');
}

export function findAccount(accounts, accountId) {
  return accounts.find(account =>
    account.id === +accountId);
}

export function isStateRestricted(state) {
  const restrictedStateFound = _.find(restrictedStates, st => st.name === state);
  return restrictedStateFound !== undefined;
}

export function isFailTransaction(transaction) {
  return (transaction.status === 'FAILED' && transaction.type !== transactionTypes.PURCHASE_NOTE
  && transaction.type !== transactionTypes.FUNDING);
}

export function isCancelTransaction(transaction) {
  return (transaction.status === 'CANCELLED' && transaction.type !== transactionTypes.PURCHASE_NOTE
  && transaction.type !== transactionTypes.FUNDING);
}

export function isPurchaseFailedTransaction(transaction) {
  return (((transaction.failureReason && transaction.failureReason.includes('R03') !== true && transaction.failureReason.includes('R01') !== true) || transaction.failureReason === null) && (transaction.status === 'FAILED' || transaction.status === 'CANCELLED') && (transaction.type === transactionTypes.PURCHASE_NOTE
  || transaction.type === transactionTypes.FUNDING));
}

export function isPurchaseFailedTransactionWithSelectedErrorsCode(transaction) {
  return (transaction.failureReason && (transaction.failureReason.includes('R03') || transaction.failureReason.includes('R01')) && (transaction.status === 'FAILED' || transaction.status === 'CANCELLED') && (transaction.type === transactionTypes.PURCHASE_NOTE
  || transaction.type === transactionTypes.FUNDING));
}

export const logglyTags = {
  auth0: 'auth0',
  dwolla: 'dwolla',
};

function validateLogArgs(options) {
  const errors = [];
  if (options.level === 'error') {
    if (!options.stack) {
      errors.push('Parameter "stack" is missing.');
    }
  } else {
    const requiredOption = ['level', 'action', 'message'];
    requiredOption.forEach((option) => {
      if (!options[option]) {
        errors.push(`Parameter "${option}" is missing.`);
      }
    });
  }

  if (errors.length > 0) {
    throw errors;
  }
}

export function logAction(options) {
  const { level, user, action, message, tags, stack } = options;
  validateLogArgs(options);
  const newLogger = new window.LogglyTracker();
  newLogger.push({
    logglyKey: logglyConfig.logglyKey,
    tag: tags ? tags.join() : '',
  });
  const data = {
    level,
    message,
    timestampUTC: momentTimezone().utc().toString(),
    timestampET: momentTimezone().tz(timeZone()).toString(),
    action: action || 'N/A',
    originSystem: logglyConfig.originSystem,
  };

  if (stack) {
    data.stack = stack;
  }
  if (user) {
    data.user = user;
  }
  newLogger.push(data);
}
export function addressAreEqual(adr1, adr2) {
  return adr1.city === adr2.city &&
    adr1.state === adr2.state &&
    adr1.streetAddress1 === adr2.streetAddress1 &&
    adr1.streetAddress2 === adr2.streetAddress2 &&
    adr1.zipCode === adr2.zipCode;
}

export function findRates(interestYearRates, term, rate, isAdmin = false) {
  const activeYearRates = [...interestYearRates].sort(
    (a, b) => a.precedence - b.precedence,
  );
  const zeroRate = activeYearRates.find(val => val.rate === 0);
  if (rate === 0 && zeroRate) {
    if (isAdmin) {
      return { term, rate };
    }
    return zeroRate;
  }
  const found = activeYearRates.reduce(
    (accum, curr) => {
      const dif1 = Math.abs(curr.term - term);
      const dif2 = Math.abs(accum.term - term);
      let validation = dif1 <= dif2;
      if (dif1 === dif2) {
        validation = Math.abs(curr.rate - rate) <= Math.abs(accum.rate - rate);
      }
      return validation ? curr : accum;
    },
    { term: Infinity, rate: Infinity },
  );
  return found;
}

export function getAccounTypeProperties(type) {
  const key = Object.keys(AccountTypes)
    .find(currentKey => AccountTypes[currentKey].name === type);
  return AccountTypes[key];
}

export function getUserTypeLabel(type) {
  const key = Object.keys(UserTypes)
    .find(currentKey => UserTypes[currentKey].name === type);
  return UserTypes[key].label;
}

export function isStringValid(value) {
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return false;
}

export function isAddressValid({
  city,
  state,
  streetAddress1,
  zipCode,
}) {
  return isStringValid(city)
    && isStringValid(state)
    && isStringValid(streetAddress1)
    && isStringValid(zipCode);
}

export function capitalize(string) {
  const newString = string.toLowerCase();
  return newString.replace(/(?:^|\s)\S/g, a => a.toUpperCase());
}

export function downloadPDF(fileName, buffer) {
  const blob = new Blob([new Uint8Array(buffer)], { type: 'application/pdf' });
  const dataUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = dataUrl;
  link.target = '_blank';
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function openUrl(url) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadCSVBlob(fileName, blob) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function downloadBlob(fileName, blob) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function downloadCSV(fileName, buffer) {
  downloadCSVBlob(fileName, new Blob([buffer], { type: 'text/csv;charset=utf-8;' }));
}

export function postMessageSuccessEmbedLogin(redirectUrl) {
  parent.postMessage({
    type: 'EMBED_LOGIN',
    redirectUrl,
  }, '*');
}

export function orderTransactions(noteTransactions) {
  return _.orderBy(noteTransactions, ['transactionId'], ['desc']);
}

export function selectRateAndTermToDisplay(
  note,
  rateAndTermList,
  ) {
  const { transaction, term: termFromNote } = note;
  const { rate, term, actionTaken } = transaction;
  const isCustomRateAndTerm = rateAndTermList
  .some(rt =>
    rt.rate === rate && rt.term === term) === false && !!actionTaken;
  if (!isCustomRateAndTerm) {
    const rateAndTerm = rateAndTermList.find((rt) => {
      if (!actionTaken) {
        return rt.term === termFromNote;
      }
      return rt.term === termFromNote && rt.rate === rate;
    });
    return {
      isCustom: isCustomRateAndTerm,
      selected: rateAndTerm,
    };
  }
  return {
    isCustom: isCustomRateAndTerm,
    selected: {
      rate,
      term,
      label: `${formatRate(rate)}, ${formatTerm(term)}`,
      id: rateAndTermList.length + 1,
      precedence: rateAndTermList.length + 1,
    },
  };
}


export function setActionTakenUpcomingTransactionHelper(actionTaken, transactionType) {
  const action = find(Actions, a => a.name === actionTaken);

  if (action) {
    return action;
  }

  switch (transactionType) {
    case transactionTypes.INT_PAID:
    case transactionTypes.SELL_MATURE:
      return Actions.cashout;
    case transactionTypes.INT_REINVEST:
    case transactionTypes.SELL_REINVEST:
      return Actions.reinvest;
    case transactionTypes.INT_DONATE:
    case transactionTypes.SELL_DONATE:
      return Actions.donate;
    default:
      return null;
  }
}

export function setFundingSourceUpcomingTransactionHelper(
  action,
  fundingSourceId,
  defaultFundingSourceId,
  fundingSources,
  ) {
  if (action && (action.name === Actions.cashout.name)) {
    const targetId = fundingSourceId !== undefined ? fundingSourceId : defaultFundingSourceId;
    return fundingSources.find(fs => fs.id.toString() === targetId.toString());
  }
  return null;
}

export function orderUpcomingTransactions(transactions) {
  return _.orderBy(transactions,
  ['transaction.noteId', 'transaction.type'],
  ['desc', 'desc']);
}

export function formatFileExtensions(extensions) {
  const uniqueExtensions = [...new Set(extensions)];

  uniqueExtensions.sort();

  // Join all but the last item with commas, and the last item with 'or'
  if (uniqueExtensions.length > 1) {
    const lastExtension = uniqueExtensions.pop();
    return `${uniqueExtensions.join(', ')} or ${lastExtension}`;
  }
  return uniqueExtensions[0];
}

export function normalizeRichTextLinks(html) {
  if (!html) {
    return '';
  }
  const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
  const anchors = Array.from(parsedHtml.body.querySelectorAll('a[href]'));

  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href').trim();
    // A href without a scheme (e.g. "www.calvertimpactcapital.org") is resolved
    // by the browser against the portal origin, so it navigates inside the app
    // instead of going to the external site.
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(href);
    const isInternalReference = href.startsWith('#') || href.startsWith('/');

    if (href && !hasScheme && !isInternalReference) {
      anchor.setAttribute('href', `https://${href}`);
    }
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });

  return parsedHtml.body.innerHTML;
}



// WEBPACK FOOTER //
// ./src/util/index.js